import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

const TratamenteContext = createContext(null);
const API_URL = import.meta.env.VITE_API_URL || "https://api.endoac.ro";

const normalizeService = (raw = {}) => ({
  id: raw.id,
  serviceId: raw.serviceId || raw.service || raw.service_id || null,
  category: raw.category || raw.cat || "",
  commissionTag: raw.commissionTag || "",
  name: raw.name || "",
  scope: raw.scope || (raw.tooth ? "tooth" : "full_mouth"),
  tooth: raw.tooth || "",
  basePrice: Number(raw.basePrice ?? 0),
  discount: Number(raw.discount ?? 0),
  finalPrice: Number(raw.finalPrice ?? raw.basePrice ?? 0),
  paidAmount: Number(raw.paidAmount ?? 0),
  done: Boolean(raw.done),
  doneAt: raw.doneAt || null,
  paid: Boolean(raw.paid),
  paidAt: raw.paidAt || null,
  createdAt: raw.createdAt || null,
  updatedAt: raw.updatedAt || null,
});

const normalizeTreatment = (raw = {}) => ({
  id: raw.id,
  patientId: raw.patientId || raw.patient || "",
  doctorId: raw.doctorId || raw.doctor || "",
  scope: raw.scope || (raw.tooth ? "tooth" : "full_mouth"),
  tooth: raw.tooth || "",
  done: Boolean(raw.done),
  notes: raw.notes || "",
  services: Array.isArray(raw.services) ? raw.services.map(normalizeService) : [],
  payment: {
    amount: Number(raw.payment?.amount ?? 0),
    hasValue: Boolean(raw.payment?.hasValue),
  },
  createdAt: raw.createdAt || null,
  updatedAt: raw.updatedAt || null,
});

export function TratamenteProvider({ children }) {
  const { token } = useAuth();
  const [tratamente, setTratamente] = useState({});
  const [loadingPatients, setLoadingPatients] = useState({});
  const [errors, setErrors] = useState({});

  const authHeaders = useMemo(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const fetchForPatient = useCallback(
    async (patientId) => {
      if (!patientId || !token) return [];

      setLoadingPatients((prev) => ({ ...prev, [patientId]: true }));
      setErrors((prev) => ({ ...prev, [patientId]: "" }));

      try {
        const res = await fetch(`${API_URL}/api/treatments/patient/${patientId}`, {
          headers: {
            ...authHeaders,
          },
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-au putut încărca tratamentele");
        }

        const list = (data?.data?.treatments || []).map(normalizeTreatment);
        setTratamente((prev) => ({ ...prev, [patientId]: list }));
        return list;
      } catch (err) {
        console.error("Eroare fetch tratamente:", err);
        setErrors((prev) => ({ ...prev, [patientId]: err.message }));
        throw err;
      } finally {
        setLoadingPatients((prev) => ({ ...prev, [patientId]: false }));
      }
    },
    [authHeaders, token]
  );

  const ensureLoaded = useCallback(
    async (patientId) => {
      if (!patientId) return [];
      if (!Array.isArray(tratamente[patientId])) {
        return fetchForPatient(patientId);
      }
      return tratamente[patientId];
    },
    [fetchForPatient, tratamente]
  );

  const addTratament = useCallback(
    async (patientId, tooth, services = [], extras = {}) => {
      if (!patientId || !token) return null;
      const payload = {
        patientId,
        tooth,
        scope: extras.scope || undefined,
        services,
        notes: extras.notes || "",
        payment: extras.payment || {},
      };

      const res = await fetch(`${API_URL}/api/treatments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-a putut adăuga tratamentul");
      }
      const treatment = normalizeTreatment(data?.data?.treatment);
      setTratamente((prev) => {
        const list = Array.isArray(prev[patientId]) ? prev[patientId] : [];
        return {
          ...prev,
          [patientId]: [treatment, ...list],
        };
      });
      return treatment;
    },
    [authHeaders, token]
  );

  const updateTratament = useCallback(
    async (patientId, tratamentId, payload) => {
      if (!patientId || !tratamentId || !token) return null;
      const res = await fetch(`${API_URL}/api/treatments/${tratamentId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-a putut actualiza tratamentul");
      }
      const treatment = normalizeTreatment(data?.data?.treatment);
      setTratamente((prev) => {
        const list = Array.isArray(prev[patientId]) ? prev[patientId] : [];
        return {
          ...prev,
          [patientId]: list.map((item) => (item.id === treatment.id ? treatment : item)),
        };
      });
      return treatment;
    },
    [authHeaders, token]
  );

  const deleteTratament = useCallback(
    async (patientId, tratamentId) => {
      if (!patientId || !tratamentId || !token) return false;
      const res = await fetch(`${API_URL}/api/treatments/${tratamentId}`, {
        method: "DELETE",
        headers: {
          ...authHeaders,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-a putut șterge tratamentul");
      }
      setTratamente((prev) => {
        const list = Array.isArray(prev[patientId]) ? prev[patientId] : [];
        return {
          ...prev,
          [patientId]: list.filter((item) => item.id !== tratamentId),
        };
      });
      fetchForPatient(patientId).catch(() => {});
      return true;
    },
    [authHeaders, token, fetchForPatient]
  );

  const toggleTratamentStatus = useCallback(
    async (patientId, tratamentId) => {
      const list = Array.isArray(tratamente[patientId]) ? tratamente[patientId] : [];
      const current = list.find((item) => item.id === tratamentId);
      if (!current) return null;
      const nextDone = !current.done;
      const updatedServices = (current.services || []).map((srv) => ({
        ...srv,
        done: nextDone,
        doneAt: nextDone
          ? srv.doneAt || new Date().toISOString()
          : null,
      }));
      return updateTratament(patientId, tratamentId, {
        done: nextDone,
        services: updatedServices,
      });
    },
    [tratamente, updateTratament]
  );

  const toggleTratamentServiceStatus = useCallback(
    async (patientId, tratamentId, serviceIndex) => {
      const list = Array.isArray(tratamente[patientId]) ? tratamente[patientId] : [];
      const current = list.find((item) => item.id === tratamentId);
      if (!current) return null;
      const service = current.services?.[serviceIndex];
      if (!service) return null;

      const res = await fetch(
        `${API_URL}/api/treatments/${tratamentId}/services/${service.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify({ done: !service.done }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-a putut actualiza serviciul");
      }
      const updatedService = normalizeService(data?.data?.service);
      setTratamente((prev) => {
        const mapped = (prev[patientId] || []).map((t) => {
          if (t.id !== tratamentId) return t;
          const services = t.services.slice();
          const idx = services.findIndex((s) => s.id === updatedService.id);
          if (idx !== -1) {
            services[idx] = updatedService;
          }
          const allDone = services.length > 0 && services.every((srv) => srv.done);
          return { ...t, services, done: allDone };
        });
        return { ...prev, [patientId]: mapped };
      });
      return updatedService;
    },
    [authHeaders, tratamente, updateTratament]
  );

  const deleteTratamentService = useCallback(
    async (patientId, tratamentId, serviceIndex) => {
      const list = Array.isArray(tratamente[patientId]) ? tratamente[patientId] : [];
      const current = list.find((item) => item.id === tratamentId);
      if (!current) return false;
      const service = current.services?.[serviceIndex];
      if (!service) return false;

      const res = await fetch(
        `${API_URL}/api/treatments/${tratamentId}/services/${service.id}`,
        {
          method: "DELETE",
          headers: {
            ...authHeaders,
          },
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-a putut șterge serviciul");
      }
      setTratamente((prev) => {
        const mapped = (prev[patientId] || []).map((t) => {
          if (t.id !== tratamentId) return t;
          const services = t.services.filter((srv) => srv.id !== service.id);
          return { ...t, services };
        });
        return { ...prev, [patientId]: mapped };
      });
      return true;
    },
    [authHeaders, tratamente]
  );

  const setTratamentPayment = useCallback(
    async (patientId, tratamentId, payment) => {
      if (!patientId || !tratamentId) return null;
      return updateTratament(patientId, tratamentId, { payment });
    },
    [updateTratament]
  );

  const registerPayment = useCallback(
    async (patientId, payload) => {
      if (!patientId || !token) return null;

      const res = await fetch(`${API_URL}/api/patients/${patientId}/payments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-a putut procesa plata");
      }

      const rawTreatment = data?.data?.treatment;
      if (rawTreatment) {
        const normalizedTreatment = normalizeTreatment(rawTreatment);
        setTratamente((prev) => {
          const list = Array.isArray(prev[patientId]) ? prev[patientId] : [];
          const exists = list.some((t) => t.id === normalizedTreatment.id);
          const updatedList = exists
            ? list.map((t) => (t.id === normalizedTreatment.id ? normalizedTreatment : t))
            : [normalizedTreatment, ...list];
          return { ...prev, [patientId]: updatedList };
        });
      }

      return data?.data || {};
    },
    [authHeaders, token]
  );

  const notifyPlanCompletion = useCallback(
    async (patientId) => {
      if (!patientId || !token) {
        throw new Error("Pacient invalid pentru notificare");
      }
      const res = await fetch(
        `${API_URL}/api/treatments/patient/${patientId}/notify-completion`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data?.message || "Nu s-a putut trimite notificarea de finalizare"
        );
      }
      return data?.message || "Notificarea a fost trimisă";
    },
    [authHeaders, token]
  );

  const addTratamentService = useCallback(
    async (patientId, tratamentId, servicePayload) => {
      if (!patientId || !tratamentId || !token) return null;
      const res = await fetch(`${API_URL}/api/treatments/${tratamentId}/services`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(servicePayload),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-a putut adăuga serviciul");
      }
      const service = normalizeService(data?.data?.service);
      setTratamente((prev) => {
        const mapped = (prev[patientId] || []).map((t) =>
          t.id === tratamentId ? { ...t, services: [...(t.services || []), service] } : t
        );
        return { ...prev, [patientId]: mapped };
      });
      return service;
    },
    [authHeaders, token]
  );

  const value = useMemo(
    () => ({
      tratamente,
      loadingPatients,
      errors,
      ensureLoaded,
      fetchForPatient,
      addTratament,
      addTratamentService,
      updateTratament,
      deleteTratament,
      toggleTratamentStatus,
      toggleTratamentServiceStatus,
      deleteTratamentService,
      setTratamentPayment,
      registerPayment,
      notifyPlanCompletion,
    }),
    [
      tratamente,
      loadingPatients,
      errors,
      ensureLoaded,
      fetchForPatient,
      addTratament,
      addTratamentService,
      updateTratament,
      deleteTratament,
      toggleTratamentStatus,
      toggleTratamentServiceStatus,
      deleteTratamentService,
      setTratamentPayment,
      registerPayment,
      notifyPlanCompletion,
    ]
  );

  return (
    <TratamenteContext.Provider value={value}>
      {children}
    </TratamenteContext.Provider>
  );
}

export const useTratamente = () => useContext(TratamenteContext);
